import {useSigma} from "@react-sigma/core";
import forceAtlas2 from 'graphology-layout-forceatlas2';
import FA2Layout from 'graphology-layout-forceatlas2/worker';
import {useEffect} from "react";
import {useSubNetworkContext} from "../../contexts/SubNetworkContext";
import {useTheCellMapContext} from "../../contexts/TheCellMapContext";
import {useAnnotationMap} from "../../hooks/useAnnotationMap";
import {useEntrezMap} from "../../hooks/useEntrezMap";
import {useFileDataset, usePostSubNetwork} from "../../hooks/useJsonLoader";
import {AppSettings} from "../../settings";
import {rotateGraphSmart} from "../../sigma/rotate";
import {UNANNOTATED_TERM_NAME} from "../../types/consts";
import {Annotation} from "../../types/Types";
import {getRandomInt} from "../../utils/random";
import {floorToDecimals} from "../../utils/number";


const SubNetworkDataController = () => {
  const {selectedGenes} = useTheCellMapContext();
  const {
    subNetworkData,
    subNetworkCutoff,
    setSubNetworkCutoff,
    setSubNetworkData,
    setMaxLoadedPcc
  } = useSubNetworkContext();
  const {setDisplayedTerms} = useSubNetworkContext();

  const sigma = useSigma();
  const graph = sigma.getGraph();

  const dataset = useFileDataset();
  const entrezMap = useEntrezMap();

  const postSubnetwork = usePostSubNetwork();

  const annotationMap = useAnnotationMap();

  /**
   * Used in the legend to display "Unannotated" entries for nodes
   * that are present in the subnetwork but have no corresponding
   * annotation in 'annotationMap'.
   */
  const UNANNOTATED_ANNOTATION: Annotation = {
    name: UNANNOTATED_TERM_NAME,
    colour: AppSettings.NODE_SUBNETWORK_COLOUR,
    entrez: [],
  };

  /**
   * Add a node to the graph if it doesn't exist yet. Set size according to isSelected. Random position.
   */
  const addNode = (nodeId: number, label: string, isSelected: boolean) => {
    if (!graph.hasNode(nodeId)) {
      const node = {
        e: nodeId,
        label: label,
        size: isSelected ? 15 : 10,
        forceLabel: isSelected,
        selected: isSelected,
        x: getRandomInt(-100, 100),
        y: getRandomInt(-100, 100),
        color: AppSettings.NODE_SUBNETWORK_COLOUR,
      };

      const annotation = annotationMap.get(nodeId);
      if (!!annotation) {
        node.color = annotation.colour;
      }

      graph.addNode(nodeId, node);
    }
  }

  useEffect(() => {
    if (!graph || !subNetworkData) return;

    graph.clear();

    /* Find all selected genes and their one-hop neighbours */
    const selectedIdsSet = new Set(selectedGenes.map(s => s.value));
    const oneHopSet = new Set<number>();
    let maxPcc = 0;

    subNetworkData.correlations.forEach(({a, b, c}) => {
      if (selectedIdsSet.has(a) || selectedIdsSet.has(b)) maxPcc = Math.max(maxPcc, c);

      if (c < subNetworkCutoff) return;

      if (selectedIdsSet.has(a)) oneHopSet.add(b);
      if (selectedIdsSet.has(b)) oneHopSet.add(a);
    });

    /* Set the max PCC value between selected genes and their one-hop neighbours. */
    setMaxLoadedPcc(floorToDecimals(maxPcc));

    /* Create a list of nodes that we want to keep in the graph, based on selected genes and their one-hop neighbours. */
    const nodesToKeep = new Set([...selectedIdsSet, ...oneHopSet]);

    subNetworkData.correlations.forEach(({a, b, c}) => {
      /* Filter out nodes that are not in the list of nodes to keep. */
      if (!nodesToKeep.has(a) && !nodesToKeep.has(b)) return;

      const from = entrezMap![a];
      const to = entrezMap![b];

      if (!from || !to) return;

      /* Filter out edges with a correlation value below the cutoff. */
      if (c < subNetworkCutoff) {
        return;
      }

      /* Create the nodes if they don't exist' */
      addNode(a, from.s, selectedIdsSet.has(a));
      addNode(b, to.s, selectedIdsSet.has(b));

      /* Create the edge */
      if (!graph.hasEdge(a, b))
        graph.addEdge(a, b, {size: 1, weight: c});
    });

    /* Iterate newly added nodes and determine which terms are displayed. */
    const terms: Annotation[] = [];
    const localUnannotated: Annotation = {...UNANNOTATED_ANNOTATION, entrez: []};
    graph.forEachNode(node => {
      const term = annotationMap.get(+node); // We know that the node is a number, so this is safe
      if (term) {
        if (!terms.includes(term)) {
          terms.push(term);
        }
      } else {
        localUnannotated.entrez.push(+node);
      }
    });
    if (localUnannotated.entrez.length > 0) {
      terms.push(localUnannotated);
    }
    setDisplayedTerms(terms);

    /* Run the layout, these settings seem reasonable for my test case. Might be completely wrong */
    const sensibleSettings = forceAtlas2.inferSettings(graph);
    forceAtlas2.assign(graph, {iterations: 100});

    const layout = new FA2Layout(graph, {
      weighted: true,
      settings: {
        ...sensibleSettings,
        edgeWeightInfluence: 2,
      }
    });

    layout.start();

    let cancelRotation = () => {};

    /* Run for 2.5s */
    const timeout = setTimeout(() => {
      if (layout.isRunning()) layout.kill();
      let {cancel} = rotateGraphSmart(sigma, .75);
      cancelRotation = cancel;
    }, 2500);

    return () => {
      clearTimeout(timeout); // Cancel the timeout if the component unmounts before layout is done
      if (layout.isRunning()) layout.kill(); // Stop any running layout
      cancelRotation(); // Cancel any ongoing rotation
      setMaxLoadedPcc(1);
    };
  }, [sigma, graph, subNetworkData, subNetworkCutoff, annotationMap]);

  useEffect(() => {
    if (!graph || !dataset || !entrezMap) return;

    /** Empty graph if no genes are selected */
    if (selectedGenes.length === 0) {
      graph.clear();

      setSubNetworkData(null);
      setMaxLoadedPcc(1);
      setDisplayedTerms([]);

      return;
    }

    postSubnetwork({
      queries: selectedGenes.map(g => g.value)
    }).then(results => {
      if (!results) return;

      setSubNetworkData(results);
      setSubNetworkCutoff(results.cutoff);
      console.log(`Loaded ${results.correlations.length} edges, cutoff: ${results.cutoff}`);
    });
  }, [graph, dataset, entrezMap, selectedGenes]);

  return null;
};

export default SubNetworkDataController;
